/**
 * The pure half of the comment-thread client (`./comments`) — the #17 payload
 * normaliser. Deliberately framework-free AND bundler-free: no `import.meta.env`
 * (`./client`), no react-query — the `unit` Playwright project imports it in
 * plain Node, the same rule `../pages/venues/venues-model` states.
 *
 * T.Z66 — this is the line where the venue thread went dark. The fetch used to
 * hand back `envelope.data` only when it was an ARRAY, and the api's ratified
 * #17 body is `{data:{comments:[…]}, meta}` (the v1.1 envelope correction, key
 * `comments` — never `data` twice), so every real answer degraded to `[]`: the
 * optimistic row vanished the moment the refetch landed and nothing survived a
 * reload, while the row sat in Postgres all along. `commentsOf` reads the nested
 * named list, keeps the bare array as the legacy/stub dialect, and only a
 * genuinely alien payload degrades to an empty thread.
 */

import type { CommentDto } from './comments';

/** True when the value looks like the `{ comments: […] }` named list. */
function isCommentList(payload: unknown): payload is { comments: CommentDto[] } {
  return typeof payload === 'object' && payload !== null && Array.isArray((payload as { comments?: unknown }).comments);
}

/**
 * The thread of one scope, from whatever #17 actually answered.
 *
 *   `{ comments: […] }`  — the contract (`CommentListEnvelope`, api.yaml v1.1)
 *   `[…]`                — the pre-v1.1 shape the Playwright stubs spoke
 *   anything else        — `[]` (a shape we did not agree on, not a crash)
 */
export function commentsOf(payload: unknown): CommentDto[] {
  if (Array.isArray(payload)) return payload;
  return isCommentList(payload) ? payload.comments : [];
}
